import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { appService } from "../../App/app.service";
import {
  getDefaultSalesType,
  getDefaultSelectedStage,
} from "../../util/customFunctions";
import { fetchFirstLevelTeamNames, fetchDefaultTeamFilter } from "../rccUtils";

/**
 * usePipelineMixData.js
 *
 * Custom hooks for fetching Open/Qualified Pipeline Mix data by dimension.
 * fetch filters, compute defaults, dispatch Redux,retrieve data, and
 * expose normalized chart data for stacked bar cards.
 *
 * Returned interface:
 * - chartData: stacked bar friendly rows by quarter and categories
 * - derivedCategories: sorted categories (legend and stack order)
 * - appliedFilters: API filters applied
 * - isLoading: whether fetching is in progress
 * - error: error message if fetch fails
 *
 */
function usePipelineMixDataByDimension(dimensionKey) {
  const dispatch = useDispatch();
  const company = useSelector((state) => state.app.company);
  const openPipelineData = useSelector(
    (state) => state.app.pipelineMixAnalysisData || null
  );

  const isDataLoaded =
    openPipelineData &&
    (Array.isArray(openPipelineData?.data)
      ? openPipelineData.data.length > 0
      : Array.isArray(openPipelineData) && openPipelineData.length > 0);

  const fetchingRef = useRef(false);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [isLoading, setIsLoading] = useState(!isDataLoaded);
  const [error, setError] = useState("");
  const [firstLevelTeamNames, setFirstLevelTeamNames] = useState([]);
  const [defaultTeamFilter, setDefaultTeamFilter] = useState([]);
  const [isFirstLevelTeamsLoaded, setIsFirstLevelTeamsLoaded] = useState(false);
  const [isDefaultTeamLoaded, setIsDefaultTeamLoaded] = useState(false);

  /**
   * Step 1: Fetch the list of first-level (parent) team names from the team hierarchy.
   * We need this before we can make the API request with team filters.
   * Uses the same source as Team cards for consistency.
   */
  useEffect(() => {
    fetchFirstLevelTeamNames().then((names) => {
      setFirstLevelTeamNames(names);
      setIsFirstLevelTeamsLoaded(true);
    });
    fetchDefaultTeamFilter().then((teams) => {
      setDefaultTeamFilter(teams);
      setIsDefaultTeamLoaded(true);
    });
  }, []);

  useEffect(() => {
    // Don't start fetching until we know which teams to filter by
    if (!isFirstLevelTeamsLoaded || !isDefaultTeamLoaded) return;
    if (isDataLoaded || fetchingRef.current) return;
    fetchingRef.current = true;
    setIsLoading(true);
    setError("");
    dispatch({ type: "get_pipeline_mix_filters_request" });

    appService.getPipelineMixAnalysisFilters().then(
      (resp) => {
        const lkpFilters = Object.values(
          resp.message.lkpFilters.reduce((r, c) => Object.assign(r, c), {})
        );
        const defaultTimeFrame = resp.message.defaultTimeFrame;
        const computedFilters = resp.message.computedFilters;

        dispatch({
          type: "get_pipeline_mix_filters_success",
          json: { message: computedFilters, lkpFilters, defaultTimeFrame },
        });

        const stageSequence = getDefaultSelectedStage(
          computedFilters.filter((f) => f.type === "stage")
        );
        const defaultStages = computedFilters
          .filter((item) => item.sequence >= stageSequence)
          .map((i) => i.stage_name)
          .filter(Boolean);

        const filters = [];
        const tfs = getDefaultSalesType(
          computedFilters.filter((i) => i.type === defaultTimeFrame),
          defaultTimeFrame
        );
        if (tfs[0] !== "All") {
          filters.push({ name: defaultTimeFrame, value: tfs, string: true });
        }
        if (defaultStages.length > 0) {
          filters.push({ name: "stage", string: true, value: defaultStages });
        }

        const defaultOppType = getDefaultSalesType(
          computedFilters.filter((i) => i.type === "Type"),
          "Type"
        );
        const defaultSolutionCat = getDefaultSalesType(
          computedFilters.filter((i) => i.type === "Solution_Cat"),
          "Solution_Cat"
        );

        if (company === "C0008") {
          const salesFilters = getDefaultSalesType(
            computedFilters.filter((s) => s.type === "cross_sell1__c"),
            "cross_sell1__c"
          );
          const dealsFilters = getDefaultSalesType(
            computedFilters.filter((s) => s.type === "Type"),
            "Type"
          );
          if (salesFilters[0] !== "All") {
            filters.push({ name: "cross_sell1__c", value: salesFilters });
          }
          if (dealsFilters[0] !== "All") {
            filters.push({ name: "Type", value: dealsFilters });
          }
        }

        if (defaultOppType[0] !== "All") {
          filters.push({ name: "Type", value: defaultOppType });
        }
        if (defaultSolutionCat[0] !== "All") {
          filters.push({ name: "Solution_Cat", value: defaultSolutionCat });
        }

        // Add team filter for first-level teams to match Team cards' data subset
        // Prioritize first-level teams, fallback to default team filter if not available
        const teamsToFilter =
          Array.isArray(firstLevelTeamNames) && firstLevelTeamNames.length > 0
            ? firstLevelTeamNames
            : (Array.isArray(defaultTeamFilter) && defaultTeamFilter.length > 0
                ? defaultTeamFilter
                : []);

        if (Array.isArray(teamsToFilter) && teamsToFilter.length > 0) {
          filters.push({
            name: "team",
            value: teamsToFilter,
            string: true,
          });
        }

        setAppliedFilters(filters);
        dispatch({ type: "current_selected_filters", filters });
        dispatch({ type: "get_pipeline_mix_analysis_data_request" });

        appService
          .getPipelineMixAnalysisData(
            computedFilters.length === 0 ? [] : filters,
            defaultTimeFrame
          )
          .then(
            (dataResp) => {
              dispatch({
                type: "get_pipeline_mix_analysis_data_success",
                json: dataResp,
              });
              fetchingRef.current = false;
              setIsLoading(false);
            },
            (err) => {
              const msg =
                typeof err === "object" ? "Something went wrong" : err;
              dispatch({
                type: "get_pipeline_mix_analysis_data_failure",
                error: msg,
              });
              setError(msg);
              fetchingRef.current = false;
              setIsLoading(false);
            }
          );
      },
      (err) => {
        const msg = typeof err === "object" ? "Something went wrong" : err;
        dispatch({ type: "get_pipeline_mix_filters_failure", error: msg });
        setError(msg);
        fetchingRef.current = false;
        setIsLoading(false);
      }
    );
  }, [
    company,
    dispatch,
    isDataLoaded,
    firstLevelTeamNames,
    isFirstLevelTeamsLoaded,
    defaultTeamFilter,
    isDefaultTeamLoaded,
  ]);

  const { chartData, derivedCategories } = useMemo(() => {
    if (!isDataLoaded) return { chartData: [], derivedCategories: [] };
    const blocks = Array.isArray(openPipelineData?.data)
      ? openPipelineData.data
      : Array.isArray(openPipelineData)
      ? openPipelineData
      : [];

    const block = blocks.find(
      (item) =>
        item.type === dimensionKey ||
        (item.data &&
          item.data.length > 0 &&
          item.data[0][dimensionKey] !== undefined)
    );
    if (!block?.data) return { chartData: [], derivedCategories: [] };

    const rawData = block.data;
    const quarterField = block.timeline || "projected_close_fiscal_quarter";

    // Helper function to normalize dimension values: null/undefined/empty → "null"
    const normalizeDimension = (value) => {
      if (value === null || value === undefined || value === "") {
        return "null";
      }
      return value;
    };

    const totals = {};
    rawData.forEach((row) => {
      const cat = normalizeDimension(row[dimensionKey]);
      totals[cat] = (totals[cat] || 0) + (row.acv || 0);
    });

    const categories = Object.keys(totals).sort(
      (a, b) => totals[b] - totals[a]
    );

    const grouped = {};
    rawData.forEach((row) => {
      const fq = row[quarterField];
      if (!fq) return;
      if (!grouped[fq]) {
        grouped[fq] = { xAxisLabel: fq };
        categories.forEach((c) => (grouped[fq][c] = 0));
      }
      const key = normalizeDimension(row[dimensionKey]);
      grouped[fq][key] = (grouped[fq][key] || 0) + (row.acv || 0);
    });

    const out = Object.values(grouped).sort((a, b) =>
      (a.xAxisLabel || "").localeCompare(b.xAxisLabel || "")
    );

    return { chartData: out, derivedCategories: categories };
  }, [openPipelineData, isDataLoaded, dimensionKey]);

  return { chartData, derivedCategories, appliedFilters, isLoading, error };
}

export function usePipelineMixLeadSourceCategoryData() {
  return usePipelineMixDataByDimension("Lead_Source_Category__c");
}

export function usePipelineMixLeadSourceData() {
  return usePipelineMixDataByDimension("Lead_Source");
}

export function usePipelineMixIndustryData() {
  return usePipelineMixDataByDimension("Acct_Industry");
}

export function usePipelineMixVerticalData() {
  return usePipelineMixDataByDimension("Acct_Vertical");
}

export function usePipelineMixCustomerSegmentData() {
  return usePipelineMixDataByDimension("Acct_Size_Segment");
}

export function usePipelineMixSalesTypeData() {
  return usePipelineMixDataByDimension("cross_sell1__c");
}

export function usePipelineMixCustomerTypeData() {
  return usePipelineMixDataByDimension("Cust_Type");
}

export function usePipelineMixOpportunityRecordTypeData() {
  return usePipelineMixDataByDimension("Opportunity_Record_Type");
}

export function usePipelineMixSolutionData() {
  return usePipelineMixDataByDimension("Solution_Cat");
}

export function usePipelineMixDealCategoryData() {
  return usePipelineMixDataByDimension("deal_category__c");
}

export function usePipelineMixProductLineData() {
  return usePipelineMixDataByDimension("Product_Line");
}
